import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { TRIAL_OFFER_EVENTS, TRIAL_OFFER_LINK_LOCATIONS } from 'src/app/constants/trial-offer.constants';
import { NewBillingState } from 'src/app/pages/new-billing/store/state';
import { BillingDetailsApiData } from 'src/app/pages/new-billing/types/api.types';
import { BillingActionsService } from 'src/app/services/billing/billing-actions.service';
import { provideAmpTrackCtx } from 'src/app/services/tracking/amp-track';
import { TrialOfferService } from 'src/app/services/trial-offer/trial-offer.service';
import { TrialOfferDialogData, TrialOfferState } from 'src/app/services/trial-offer/trial-offer.types';

@Component({
  selector: 'app-trial-offer-dialog',
  templateUrl: './trial-offer-dialog.component.html',
  styleUrls: ['./trial-offer-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // MatDialog renders through the CDK overlay with the root injector, so the URL-driven
  // AmpTrackCtx never reaches this template.
  providers: [provideAmpTrackCtx('billing')],
})
export class TrialOfferDialogComponent implements OnInit {
  @Select(NewBillingState.updatedBillingDetails) billingDetails$: Observable<BillingDetailsApiData>;

  state: TrialOfferState = 'offer';
  submitting = false;
  errorKey: string | null = null;
  trialExpiry: string | null = null;

  constructor(
    private readonly dialogRef: MatDialogRef<TrialOfferDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly data: TrialOfferDialogData,
    private readonly trialOffer: TrialOfferService,
    private readonly billingActions: BillingActionsService,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.billingDetails$.pipe(take(1)).subscribe(billingDetails => {
      this.state = this.trialOffer.getState(billingDetails);
      this.trialExpiry = billingDetails?.trialOffer?.trialExpiry ?? null;
      this.track(TRIAL_OFFER_EVENTS.viewed);
      this.cdr.markForCheck();
    });
  }

  onActivate(): void {
    if (this.submitting) {
      return;
    }
    this.submitting = true;
    this.errorKey = null;
    this.cdr.markForCheck();

    this.trialOffer.activate(this.data.offerKey).subscribe({
      next: () => {
        this.billingActions.openSnackbar({ message: this.translate.instant('trialOffer.snackbar.activated') });
        this.dialogRef.close();
      },
      error: (error: HttpErrorResponse) => {
        this.submitting = false;
        this.errorKey = `trialOffer.errors.${this.trialOffer.readErrorCode(error) ?? 'unknown'}`;
        this.cdr.markForCheck();
      },
    });
  }

  onDismiss(): void {
    this.track(TRIAL_OFFER_EVENTS.dismissed);
    this.dialogRef.close();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onViewPlans(): void {
    this.dialogRef.close();
    void this.router.navigate(['/new-billing'], { queryParams: { choosePlan: 'yes', url: this.router.url } });
  }

  private track(event: string): void {
    this.trialOffer.track(event, {
      offerkey: this.data.offerKey,
      state: this.state,
      linklocation: TRIAL_OFFER_LINK_LOCATIONS.offerDialog,
    });
  }
}
